/**
 * @license Codistica
 *
 * Copyright (c) 2020, Codistica and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE.md file in the root directory of this source tree.
 *
 * BUILD DATE: Fri Jul 10 2020 16:51:08 GMT+0200 (Central European Summer Time)
 */
import _objectSpread from "@babel/runtime-corejs3/helpers/esm/objectSpread2";
import _someInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/some";
import _Promise from "@babel/runtime-corejs3/core-js-stable/promise";
import _Map from "@babel/runtime-corejs3/core-js-stable/map";
import _forEachInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/for-each";
import _typeof from "@babel/runtime-corejs3/helpers/esm/typeof";
import _newArrowCheck from "@babel/runtime-corejs3/helpers/esm/newArrowCheck";
import _keysInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/keys";
import _reduceInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/reduce";
import _bindInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/bind";
import _classCallCheck from "@babel/runtime-corejs3/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime-corejs3/helpers/esm/createClass";
import _defineProperty from "@babel/runtime-corejs3/helpers/esm/defineProperty";

/** @module react/classes/input-validator-plugin-utils */
import { promiseUtils, objectUtils, createHeartbeatTimeout } from '@codistica/core';
import { Types } from '@codistica/types';
var inputValidatorPluginUtilsSchema = new Types({
  options: {
    type: 'Object',
    def: {
      keys: {
        type: 'Array<string>',
        def: []
      },
      enableDeferCache: {
        type: 'boolean',
        def: false
      },
      deferThrottlingDelay: {
        type: ['number', 'null'],
        def: null
      }
    }
  }
});

/**
 * @typedef inputValidatorPluginUtilsOptionsType
 * @property {Array<string>} [keys=[]] - Validation keys.
 * @property {boolean} [enableDeferCache=false] - Enable internal defer cache.
 * @property {(number|null)} [deferThrottlingDelay=null] - Internal defer throttling delay in milliseconds.
 */

/**
 * @classdesc Input plugins utility class.
 */
var InputValidatorPluginUtils = /*#__PURE__*/function () {
  /**
   * @description Constructor.
   * @param {inputValidatorPluginUtilsOptionsType} [options] - Options.
   */
  function InputValidatorPluginUtils(options) {
    var _context, _context2, _context3, _context4, _context5, _context6, _context7, _context8, _context9, _context10, _context11, _context12, _context13;

    _classCallCheck(this, InputValidatorPluginUtils);

    _defineProperty(this, "options", void 0);

    _defineProperty(this, "value", void 0);

    _defineProperty(this, "previousValue", void 0);

    _defineProperty(this, "validatorOutput", void 0);

    _defineProperty(this, "deferContexts", void 0);

    _defineProperty(this, "deferCaches", void 0);

    _defineProperty(this, "deferHeartbeats", void 0);

    var _inputValidatorPlugin = inputValidatorPluginUtilsSchema.validate({
      options: options
    });

    options = _inputValidatorPlugin.options;
    this.options = options;
    this.value = '';
    this.previousValue = null;
    this.validatorOutput = {
      result: null,
      report: {},
      messages: {},
      data: {},
      promises: {}
    };
    this.deferContexts = {};
    this.deferCaches = {};
    this.deferHeartbeats = {}; // BIND METHODS

    this.init = _bindInstanceProperty(_context = this.init).call(_context, this);
    this.invalidate = _bindInstanceProperty(_context2 = this.invalidate).call(_context2, this);
    this.invalidateAll = _bindInstanceProperty(_context3 = this.invalidateAll).call(_context3, this);
    this.validate = _bindInstanceProperty(_context4 = this.validate).call(_context4, this);
    this.validateAll = _bindInstanceProperty(_context5 = this.validateAll).call(_context5, this);
    this.disable = _bindInstanceProperty(_context6 = this.disable).call(_context6, this);
    this.disableAll = _bindInstanceProperty(_context7 = this.disableAll).call(_context7, this);
    this.defer = _bindInstanceProperty(_context8 = this.defer).call(_context8, this);
    this.createDeferContext = _bindInstanceProperty(_context9 = this.createDeferContext).call(_context9, this);
    this.isStandBy = _bindInstanceProperty(_context10 = this.isStandBy).call(_context10, this);
    this.isValid = _bindInstanceProperty(_context11 = this.isValid).call(_context11, this);
    this.setData = _bindInstanceProperty(_context12 = this.setData).call(_context12, this);
    this.getValidatorOutput = _bindInstanceProperty(_context13 = this.getValidatorOutput).call(_context13, this);
  }
  /**
   * @instance
   * @description Initializes validator state.
   * @param {string} value - Input value.
   * @param {(boolean|null)} initialValidationStatus - Initial validation status value.
   * @param {Object<string,(string|(function(*): string|null)|Object<string,*>|null)>} [rawMessages] - Raw messages.
   * @param {Object<string,*>} [params] - Message parameters.
   * @returns {void} Void.
   */


  _createClass(InputValidatorPluginUtils, [{
    key: "init",
    value: function init(value, initialValidationStatus, rawMessages, params) {
      var _context14,
          _this = this,
          _context15;

      this.value = value;
      this.validatorOutput.result = initialValidationStatus;
      this.validatorOutput.report = _reduceInstanceProperty(_context14 = _keysInstanceProperty(this.options)).call(_context14, _bindInstanceProperty(_context15 = function _context15(acc, key) {
        _newArrowCheck(this, _this);

        acc[key] = initialValidationStatus;
        return acc;
      }).call(_context15, this), {});

      if (rawMessages) {
        var _context16, _context17;

        this.validatorOutput.messages = _reduceInstanceProperty(_context16 = _keysInstanceProperty(this.options)).call(_context16, _bindInstanceProperty(_context17 = function _context17(acc, key) {
          _newArrowCheck(this, _this);

          var rawMessage = rawMessages[key];

          if (rawMessage) {
            if (_typeof(params) === 'object' && params !== null && Object.hasOwnProperty.call(params, key)) {
              acc[key] = InputValidatorPluginUtils.createMessageObject(rawMessage, params[key]);
            } else {
              acc[key] = InputValidatorPluginUtils.createMessageObject(rawMessage, params);
            }
          }

          return acc;
        }).call(_context17, this), {});
      } else {
        this.validatorOutput.messages = {};
      }

      this.validatorOutput.data = {};
    }
    /**
     * @instance
     * @description Sets specified result to key.
     * @param {string} [key] - Key to be invalidated.
     * @param {(boolean|null)} [result] - Result.
     * @param {(string|(function(*): string|null)|Object<string,*>|null)} [rawMessage] - Raw message.
     * @param {Object<string,*>} [params] - Message parameters.
     * @param {boolean} [noAbort] - Do not abort deferred validation.
     * @returns {void} Void.
     */

  }, {
    key: "setKeyResult",
    value: function setKeyResult(key, result, rawMessage, params, noAbort) {
      if (key) {
        if (!noAbort) {
          this.deferContexts[key] && this.deferContexts[key].abort();
        }

        this.validatorOutput.report[key] = result;

        if (rawMessage) {
          this.validatorOutput.messages[key] = InputValidatorPluginUtils.createMessageObject(rawMessage, params);
        }

        this.updateResult();
      } else if (!_keysInstanceProperty(this.options).length) {
        this.validatorOutput.result = result;
      }
    }
    /**
     * @instance
     * @description Invalidates specified key or complete validation.
     * @param {string} [key] - Key to be invalidated.
     * @param {(string|(function(*): string|null)|Object<string,*>|null)} [rawMessage] - Raw message.
     * @param {Object<string,*>} [params] - Message parameters.
     * @returns {void} Void.
     */

  }, {
    key: "invalidate",
    value: function invalidate(key, rawMessage, params) {
      this.setKeyResult(key, false, rawMessage, params);
    }
    /**
     * @instance
     * @description Invalidates all keys.
     * @param {(string|(function(*): string|null)|Object<string,*>|null)} [rawMessage] - Raw message.
     * @param {Object<string,*>} [params] - Message parameters.
     * @returns {void} Void.
     */

  }, {
    key: "invalidateAll",
    value: function invalidateAll(rawMessage, params) {
      var _this2 = this;

      if (!_keysInstanceProperty(this.options).length) {
        this.validatorOutput.result = false;
      } else {
        var _context18, _context19;

        _forEachInstanceProperty(_context18 = _keysInstanceProperty(this.options)).call(_context18, _bindInstanceProperty(_context19 = function _context19(currentKey) {
          _newArrowCheck(this, _this2);

          return this.setKeyResult(currentKey, false, rawMessage, params);
        }).call(_context19, this));
      }
    }
    /**
     * @instance
     * @description Validates specified key or complete validation.
     * @param {string} [key] - Key to be validated.
     * @param {(string|(function(*): string|null)|Object<string,*>|null)} [rawMessage] - Raw message.
     * @param {Object<string,*>} [params] - Message parameters.
     * @returns {void} Void.
     */

  }, {
    key: "validate",
    value: function validate(key, rawMessage, params) {
      this.setKeyResult(key, true, rawMessage, params);
    }
    /**
     * @instance
     * @description Validates all keys.
     * @param {(string|(function(*): string|null)|Object<string,*>|null)} [rawMessage] - Raw message.
     * @param {Object<string,*>} [params] - Message parameters.
     * @returns {void} Void.
     */

  }, {
    key: "validateAll",
    value: function validateAll(rawMessage, params) {
      var _this3 = this;

      if (!_keysInstanceProperty(this.options).length) {
        this.validatorOutput.result = true;
      } else {
        var _context20, _context21;

        _forEachInstanceProperty(_context20 = _keysInstanceProperty(this.options)).call(_context20, _bindInstanceProperty(_context21 = function _context21(currentKey) {
          _newArrowCheck(this, _this3);

          return this.setKeyResult(currentKey, true, rawMessage, params);
        }).call(_context21, this));
      }
    }
    /**
     * @instance
     * @description Disables specified key or complete validation.
     * @param {string} [key] - Key.
     * @param {(string|(function(*): string|null)|Object<string,*>|null)} [rawMessage] - Raw message.
     * @param {Object<string,*>} [params] - Message parameters.
     * @returns {void} Void.
     */

  }, {
    key: "disable",
    value: function disable(key, rawMessage, params) {
      this.setKeyResult(key, null, rawMessage, params);
    }
    /**
     * @instance
     * @description Disables all keys.
     * @param {(string|(function(*): string|null)|Object<string,*>|null)} [rawMessage] - Raw message.
     * @param {Object<string,*>} [params] - Message parameters.
     * @returns {void} Void.
     */

  }, {
    key: "disableAll",
    value: function disableAll(rawMessage, params) {
      var _this4 = this;

      if (!_keysInstanceProperty(this.options).length) {
        this.validatorOutput.result = null;
      } else {
        var _context22, _context23;

        _forEachInstanceProperty(_context22 = _keysInstanceProperty(this.options)).call(_context22, _bindInstanceProperty(_context23 = function _context23(currentKey) {
          _newArrowCheck(this, _this4);

          return this.setKeyResult(currentKey, null, rawMessage, params);
        }).call(_context23, this));
      }
    }
    /**
     * @instance
     * @description Defer specified key validation.
     * @param {string} key - Key.
     * @param {(string|(function(*): string|null)|Object<string,*>|null)} [rawMessage] - Raw message.
     * @param {Object<string,*>} [params] - Message parameters.
     * @param {function(string,Object<string,*>): void} callback - Callback.
     * @returns {void} Void.
     */

  }, {
    key: "defer",
    value: function defer(key, rawMessage, params, callback) {
      var _this5 = this;

      if (this.deferContexts[key] && this.deferContexts[key].isActive() && this.value === this.previousValue) {
        this.setKeyResult(key, null, rawMessage, params, true);
        return;
      }

      this.deferContexts[key] && this.deferContexts[key].abort();

      if (this.options.enableDeferCache && this.deferCaches[key] && this.deferCaches[key].has(this.value)) {
        var cache = this.deferCaches[key].get(this.value);

        if (cache.result) {
          this.validate(key, cache.rawMessage, cache.params);
        } else if (cache.result === false) {
          this.invalidate(key, cache.rawMessage, cache.params);
        } else {
          this.disable(key, cache.rawMessage, cache.params);
        }
      } else {
        var _context24;

        if (this.options.enableDeferCache && !this.deferCaches[key]) {
          this.deferCaches[key] = new _Map();
        } // CREATE VALIDATION PROMISE


        this.validatorOutput.promises[key] = promiseUtils.createStatePromise(new _Promise(_bindInstanceProperty(_context24 = function _context24(resolve) {
          var _this6 = this;

          _newArrowCheck(this, _this5);

          var context = this.createDeferContext(key, resolve, this.deferCaches[key]);
          this.deferContexts[key] = context;

          if (this.options.deferThrottlingDelay) {
            var _context25;

            // WITH THROTTLING
            if (!this.deferHeartbeats[key]) {
              this.deferHeartbeats[key] = createHeartbeatTimeout();
            }

            this.deferHeartbeats[key](_bindInstanceProperty(_context25 = function _context25() {
              _newArrowCheck(this, _this6);

              context.updateValue();
              callback(this.value, context);
            }).call(_context25, this), this.options.deferThrottlingDelay);
          } else {
            // WITHOUT THROTTLING
            callback(this.value, context);
          }
        }).call(_context24, this)));
        this.setKeyResult(key, null, rawMessage, params, true);
      }
    }
    /**
     * @instance
     * @description Creates context for defer callback execution.
     * @param {string} key - Key.
     * @param {function(boolean): void} resolve - Promise resolve method.
     * @param {Map<string,(boolean|null)>} [cache] - Defer cache.
     * @returns {Object<string,*>} Defer context.
     */

  }, {
    key: "createDeferContext",
    value: function createDeferContext(key, resolve, cache) {
      var _this7 = this,
          _context26,
          _context27,
          _context28,
          _context29,
          _context30,
          _context31;

      var _isActive = true;
      var value = this.value;
      return {
        invalidate: _bindInstanceProperty(_context26 = function invalidate(rawMessage, params) {
          _newArrowCheck(this, _this7);

          cache && cache.set(value, {
            result: false,
            rawMessage: rawMessage,
            params: params
          });

          if (_isActive) {
            _isActive = false;
            this.invalidate(key, rawMessage, params);
            resolve(true);
            delete this.validatorOutput.promises[key];
          }
        }).call(_context26, this),
        validate: _bindInstanceProperty(_context27 = function validate(rawMessage, params) {
          _newArrowCheck(this, _this7);

          cache && cache.set(value, {
            result: true,
            rawMessage: rawMessage,
            params: params
          });

          if (_isActive) {
            _isActive = false;
            this.validate(key, rawMessage, params);
            resolve(true);
            delete this.validatorOutput.promises[key];
          }
        }).call(_context27, this),
        disable: _bindInstanceProperty(_context28 = function disable(rawMessage, params) {
          _newArrowCheck(this, _this7);

          cache && cache.set(value, {
            result: null,
            rawMessage: rawMessage,
            params: params
          });

          if (_isActive) {
            _isActive = false;
            this.disable(key, rawMessage, params);
            resolve(true);
            delete this.validatorOutput.promises[key];
          }
        }).call(_context28, this),
        abort: _bindInstanceProperty(_context29 = function abort() {
          _newArrowCheck(this, _this7);

          if (_isActive) {
            _isActive = false;
            resolve(false);
            delete this.validatorOutput.promises[key];
          }
        }).call(_context29, this),
        isActive: _bindInstanceProperty(_context30 = function isActive() {
          _newArrowCheck(this, _this7);

          return _isActive;
        }).call(_context30, this),
        updateValue: _bindInstanceProperty(_context31 = function updateValue() {
          _newArrowCheck(this, _this7);

          value = this.value;
        }).call(_context31, this)
      };
    }
    /**
     * @instance
     * @description Returns true if there are pending active defer contexts and false otherwise.
     * @returns {boolean} Result.
     */

  }, {
    key: "isStandBy",
    value: function isStandBy() {
      var _context32,
          _this8 = this,
          _context33;

      return _someInstanceProperty(_context32 = objectUtils.getValuesArray(this.deferContexts)).call(_context32, _bindInstanceProperty(_context33 = function _context33(context) {
        _newArrowCheck(this, _this8);

        return context.isActive();
      }).call(_context33, this));
    }
    /**
     * @instance
     * @description Returns specified key has validation status.
     * @param {string} [key] - Key.
     * @returns {(boolean|null)} Result.
     */

  }, {
    key: "isValid",
    value: function isValid(key) {
      if (key) {
        return this.validatorOutput.report[key];
      }

      return this.validatorOutput.result;
    }
    /**
     * @instance
     * @description Saves custom data item.
     * @param {string} key - Data key.
     * @param {*} value - Data value.
     * @returns {void} Void.
     */

  }, {
    key: "setData",
    value: function setData(key, value) {
      this.validatorOutput.data[key] = value;
    }
    /**
     * @instance
     * @description Updates validation result value according to current reports.
     * @returns {void} Void.
     */

  }, {
    key: "updateResult",
    value: function updateResult() {
      if (this.isStandBy()) {
        this.validatorOutput.result = null;
        return;
      }

      var result = null;

      for (var i in this.validatorOutput.report) {
        if (!Object.hasOwnProperty.call(this.validatorOutput.report, i)) {
          continue;
        }

        if (result === null) {
          result = this.validatorOutput.report[i];
        } else if (result === true && this.validatorOutput.report[i] !== null) {
          result = this.validatorOutput.report[i];
        }

        if (result === false) {
          break;
        }
      }

      this.validatorOutput.result = result;
    }
    /**
     * @instance
     * @description Returns validator output.
     * @returns {Object<string,*>} Validator output.
     */

  }, {
    key: "getValidatorOutput",
    value: function getValidatorOutput() {
      this.previousValue = this.value;
      return this.validatorOutput;
    }
    /**
     * @description Creates a message object.
     * @param {(string|(function(*): string|null)|Object<string,*>|null)} rawMessage - Raw message.
     * @param {Object<string,*>} params - Message parameters.
     * @param {Object<string,*>} options - Message options.
     * @returns {Object<string,*>} Message object.
     */

  }], [{
    key: "createMessageObject",
    value: function createMessageObject(rawMessage, params, options) {
      var messageSource = null;
      var messageParams = {};
      var messageOptions = {};
      var outputMessage = null;
      var outputParams = {};
      var outputOptions = {};

      if (typeof rawMessage === 'string') {
        return {
          message: rawMessage,
          params: params || {},
          options: options || {}
        };
      }

      if (_typeof(rawMessage) === 'object' && rawMessage !== null) {
        messageSource = rawMessage.message || null;
        messageParams = rawMessage.params || {};
        messageOptions = rawMessage.options || {};
      } else {
        messageSource = rawMessage;
      }

      outputParams = _objectSpread({}, params || {}, {}, messageParams);
      outputOptions = _objectSpread({}, options || {}, {}, messageOptions);

      if (typeof messageSource === 'function') {
        outputMessage = messageSource(outputParams);
      } else {
        outputMessage = messageSource;
      }

      return {
        message: outputMessage || '',
        params: outputParams,
        options: outputOptions
      };
    }
  }]);

  return InputValidatorPluginUtils;
}();

export { InputValidatorPluginUtils };